import Vue from 'vue'
import Router from 'vue-router'

// Containers
const TheContainer = () => import('@/containers/TheContainer')

// Views
const Login = () => import('@/views/Login')
const PrivacyPolicy = () => import('@/views/PrivacyPolicy')

const Wines = () => import('@/views/Wines/WinesList')
const WinesAdd = () => import('@/views/Wines/WinesAdd')
const WinesEdit = () => import('@/views/Wines/WinesEdit')

const Experiences = () => import('@/views/Experiences/ExperiencesList')
const ExperiencesAdd = () => import('@/views/Experiences/ExperiencesAdd')
const ExperiencesEdit = () => import('@/views/Experiences/ExperiencesEdit')
const ExperiencesPriceSchedule = () => import('@/views/Experiences/ExperiencePriceSchedule')

const Guides = () => import('@/views/Guides/GuidesList')
const GuidesAdd = () => import('@/views/Guides/GuidesAdd')
const GuidesEdit = () => import('@/views/Guides/GuidesEdit')

const Reservations = () => import('@/views/Reservations/ReservationsList')
const ReservationsAdd = () => import('@/views/Reservations/ReservationsAdd')
const ReservationsEdit = () => import('@/views/Reservations/ReservationsEdit')

const Statistics = () => import('@/views/Statistics/StatisticsDashboard')

const Members = () => import('@/views/Members/MembersList')
const MembersAdd = () => import('@/views/Members/MembersAdd')
const MembersEdit = () => import('@/views/Members/MembersEdit')

const Prizes = () => import('@/views/Prizes/PrizesList')
const PrizesAdd = () => import('@/views/Prizes/PrizesAdd')
const PrizesEdit = () => import('@/views/Prizes/PrizesEdit')

const Heros = () => import('@/views/Heros/HerosList')
const HerosAdd = () => import('@/views/Heros/HerosAdd')
const HerosEdit = () => import('@/views/Heros/HerosEdit')

const KeyValues = () => import('@/views/KeyValues/KeyValuesList')
const KeyValuesAdd = () => import('@/views/KeyValues/KeyValuesAdd')
const KeyValuesEdit = () => import('@/views/KeyValues/KeyValuesEdit')

const StaticInfos = () => import('@/views/StaticInfos/StaticInfosList')
const StaticInfosAdd = () => import('@/views/StaticInfos/StaticInfosAdd')
const StaticInfosEdit = () => import('@/views/StaticInfos/StaticInfosEdit')

const ExperienceAvailabilities = () => import('@/views/ExperienceAvailabilities/ExperienceAvailabilitiesList')
const ExperienceAvailabilitiesAdd = () => import('@/views/ExperienceAvailabilities/ExperienceAvailabilitiesAdd')
const ExperienceAvailabilitiesEdit = () => import('@/views/ExperienceAvailabilities/ExperienceAvailabilitiesEdit')

const AvailabilityIntervals = () => import('@/views/AvailabilityIntervals/AvailabilityIntervalsList')
const AvailabilityIntervalsAdd = () => import('@/views/AvailabilityIntervals/AvailabilityIntervalsAdd')
const AvailabilityIntervalsEdit = () => import('@/views/AvailabilityIntervals/AvailabilityIntervalsEdit')

const SatisfactionQuestions = () => import('@/views/SatisfactionQuestions/SatisfactionQuestionsList')
const SatisfactionFormAnswers = () => import('@/views/SatisfactionFormAnswers/SatisfactionFormAnswersList')

const Vouchers = () => import('@/views/Vouchers/VouchersList')
const VouchersAdd = () => import('@/views/Vouchers/VouchersAdd')
const VouchersEdit = () => import('@/views/Vouchers/VouchersEdit')

const Settings = () => import('@/views/Settings')
const Profile = () => import('@/views/Profile')
const Page404 = () => import('@/views/Page404')
const Page500 = () => import('@/views/Page500')

Vue.use(Router)

// Pages allowed for User role (EcommerceManager)
const USER_ALLOWED_ROUTES = [
    'VouchersList',
    'VouchersAdd',
    'VouchersEdit',
    'GuidesList',
    'GuidesAdd',
    'GuidesEdit',
    'ReservationsList',
    'ReservationsAdd',
    'ReservationsEdit',
    'ExperiencesList',
    'ExperiencesAdd',
    'ExperiencesEdit',
    'ExperiencesPriceSchedule',
    'ExperienceAvailabilitiesList',
    'ExperienceAvailabilitiesAdd',
    'ExperienceAvailabilitiesEdit',
    'AvailabilityIntervalsList',
    'AvailabilityIntervalsAdd',
    'AvailabilityIntervalsEdit',
    'Statistics',
    'MembersList',
    'MembersAdd',
    'MembersEdit',
    'SatisfactionQuestions',
    'SatisfactionFormAnswers',
    'Profile',
    '404',
    '500'
];

const router = new Router({
  mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'active',
  scrollBehavior: () => ({ y: 0 }),
  routes: configRoutes()
})

// Add role-based route guard
router.beforeEach((to, from, next) => {
  const loggedIn = localStorage.getItem('user');
  const publicPages = ['/login', '/reset-password', '/privacypolicy'];
  
  // Allow logout for all users (handled in main.js)
  if (to.path === '/logout') {
    return next();
  }
  
  // Check if route requires authentication
  const authRequired = !publicPages.includes(to.path);
  
  if (authRequired && !loggedIn) {
    return next('/login');
  }
  
  // If logged in, check role-based access
  if (authRequired && loggedIn) {
    try {
      const store = require('@/store').default;
      const isAdmin = store.getters['users/isAdmin'];
      const isUser = store.getters['users/isUser'];
      
      // Handle root redirect based on role
      if (to.path === '/' || to.path === '/vinhos') {
        if (isUser) {
          return next('/vouchers/lista');
        }
        if (isAdmin) {
          // Admin can access wines, so allow
          if (to.path === '/') {
            return next('/vinhos');
          }
        }
      }
      
      // Admin has access to all routes
      if (isAdmin) {
        return next();
      }
      
      // User role (EcommerceManager) only has access to specific routes
      if (isUser) {
        if (USER_ALLOWED_ROUTES.includes(to.name) || to.name === '404' || to.name === '500') {
          return next();
        } else {
          // Redirect to first allowed page
          return next('/vouchers/lista');
        }
      }
    } catch (e) {
      // If store is not ready, allow navigation (will be checked on component level)
      console.warn('Store not ready for role check:', e);
    }
  }
  
  // Redirect to dashboard if logged in and trying to access login page
  const authNotRequired = publicPages.includes(to.path);
  if (authNotRequired && loggedIn) {
    return next('/');
  }
  
  next();
});

export default router

function configRoutes () {
  return [
    {
      path: '/',
      redirect: '/vinhos',
      name: 'Wines',
      component: TheContainer,
      children: [
        // Wines
        {
          path: 'vinhos',
          redirect: '/vinhos/lista',
          name: 'Wines',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'WinesList',
              component: Wines
            },
            {
              path: 'adicionar',
              name: 'WinesAdd',
              component: WinesAdd
            },
            {
              path: 'editar',
              name: 'WinesEdit',
              component: WinesEdit
            },
          ]
        },
        // Experiences
        {
          path: 'experiencias',
          redirect: '/experiencias/lista',
          name: 'Experiences',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'ExperiencesList',
              component: Experiences
            },
            {
              path: 'adicionar',
              name: 'ExperiencesAdd',
              component: ExperiencesAdd
            },
            {
              path: 'editar',
              name: 'ExperiencesEdit',
              component: ExperiencesEdit
            },
            {
              path: 'calendario-precos',
              name: 'ExperiencesPriceSchedule',
              component: ExperiencesPriceSchedule
            },
          ]
        },
        // Guides
        {
          path: 'guias',
          redirect: '/guias/lista',
          name: 'Guides',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'GuidesList',
              component: Guides
            },
            {
              path: 'adicionar',
              name: 'GuidesAdd',
              component: GuidesAdd
            },
            {
              path: 'editar',
              name: 'GuidesEdit',
              component: GuidesEdit
            },
          ]
        },
        // Reservations
        {
          path: 'reservas',
          redirect: '/reservas/lista',
          name: 'Reservations',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'ReservationsList',
              component: Reservations
            },
            {
              path: 'adicionar',
              name: 'ReservationsAdd',
              component: ReservationsAdd
            },
            {
              path: 'editar',
              name: 'ReservationsEdit',
              component: ReservationsEdit
            },
          ]
        },
        // Statistics
        {
          path: 'estatisticas',
          name: 'Statistics',
          component: Statistics
        },
        // Members
        {
          path: 'membros',
          redirect: '/membros/lista',
          name: 'Members',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'MembersList',
              component: Members
            },
            {
              path: 'adicionar',
              name: 'MembersAdd',
              component: MembersAdd
            },
            {
              path: 'editar',
              name: 'MembersEdit',
              component: MembersEdit
            },
          ]
        },
        // Prizes
        {
          path: 'premios',
          redirect: '/premios/lista',
          name: 'Prizes',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'PrizesList',
              component: Prizes
            },
            {
              path: 'adicionar',
              name: 'PrizesAdd',
              component: PrizesAdd
            },
            {
              path: 'editar',
              name: 'PrizesEdit',
              component: PrizesEdit
            },
          ]
        },
        // Heros
        {
          path: 'capa',
          redirect: '/capa/lista',
          name: 'Heros',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'HerosList',
              component: Heros
            },
            {
              path: 'adicionar',
              name: 'HerosAdd',
              component: HerosAdd
            },
            {
              path: 'editar',
              name: 'HerosEdit',
              component: HerosEdit
            },
          ]
        },
        // KeyValues
        {
          path: 'informacoes',
          redirect: '/informacoes/lista',
          name: 'KeyValues',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'KeyValuesList',
              component: KeyValues
            },
            {
              path: 'adicionar',
              name: 'KeyValuesAdd',
              component: KeyValuesAdd
            },
            {
              path: 'editar',
              name: 'KeyValuesEdit',
              component: KeyValuesEdit
            },
          ]
        },
        // StaticInfos
        {
          path: 'paginas',
          redirect: '/paginas/lista',
          name: 'StaticInfos',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'StaticInfosList',
              component: StaticInfos
            },
            {
              path: 'adicionar',
              name: 'StaticInfosAdd',
              component: StaticInfosAdd
            },
            {
              path: 'editar',
              name: 'StaticInfosEdit',
              component: StaticInfosEdit
            },
          ]
        },
        // ExperienceAvailabilities
        {
          path: 'horarios',
          redirect: '/horarios/lista',
          name: 'ExperienceAvailabilities',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'ExperienceAvailabilitiesList',
              component: ExperienceAvailabilities
            },
            {
              path: 'adicionar',
              name: 'ExperienceAvailabilitiesAdd',
              component: ExperienceAvailabilitiesAdd
            },
            {
              path: 'editar',
              name: 'ExperienceAvailabilitiesEdit',
              component: ExperienceAvailabilitiesEdit
            },
          ]
        },
        // AvailabilityIntervals
        {
          path: 'intervalos-horarios',
          redirect: '/intervalos-horarios/lista',
          name: 'AvailabilityIntervals',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'AvailabilityIntervalsList',
              component: AvailabilityIntervals
            },
            {
              path: 'adicionar',
              name: 'AvailabilityIntervalsAdd',
              component: AvailabilityIntervalsAdd
            },
            {
              path: 'editar',
              name: 'AvailabilityIntervalsEdit',
              component: AvailabilityIntervalsEdit
            },
          ]
        },
        // SatisfactionQuestions
        {
          path: 'inqueritos',
          name: 'SatisfactionQuestions',
          component: SatisfactionQuestions
        },
        // SatisfactionFormAnswers
        {
          path: 'respostas-inqueritos',
          name: 'SatisfactionFormAnswers',
          component: SatisfactionFormAnswers
        },
        // Vouchers
        {
          path: 'vouchers',
          redirect: '/vouchers/lista',
          name: 'Vouchers',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'lista',
              name: 'VouchersList',
              component: Vouchers
            },
            {
              path: 'adicionar',
              name: 'VouchersAdd',
              component: VouchersAdd
            },
            {
              path: 'editar',
              name: 'VouchersEdit',
              component: VouchersEdit
            },
          ]
        },
        // Settings
        {
          path: 'definicoes',
          name: 'Settings',
          component: Settings
        },
        // Profile
        {
          path: 'perfil',
          name: 'Profile',
          component: Profile
        }
      ]
    },
    {
      path: '/login',
      name: 'Login',
      component: Login
    },
    {
      path: '/privacypolicy',
      name: 'PrivacyPolicy',
      component: PrivacyPolicy
    },
    {
      path: '/404',
      name: '404',
      component: Page404
    },    
    {
      path: '/500',
      name: '500',
      component: Page500
    }, 
  ]
}

